import axios from "axios"
import _ from "lodash"
import { toast } from "react-toastify"
import { createRevealAnswerOutbox } from "./revealAnswerOutbox"
import { VIEW_AS } from "../consts/viewAs"
import { isViewingAs } from "./viewAs"

// Lazy imports to break circular dependency
let Store = null
let signOut = null
let toggleSingeDeviceNotification = null

const getStore = () => {
	if (!Store) {
		Store = require("../store").default
	}
	return Store
}

const getAuthActions = () => {
	if (!signOut) {
		const authActions = require("../store/actions/authActions")
		signOut = authActions.signOut
		toggleSingeDeviceNotification = authActions.toggleSingeDeviceNotification
	}
	return { signOut, toggleSingeDeviceNotification }
}

// CACHING DISABLED - All requests go directly to the server for live database results
// This ensures admins always see their changes immediately
// To re-enable caching in the future, restore the caching logic that was here

// Deduplicate concurrent identical requests (prevents double-fetching, not caching)
const pendingRequests = new Map()

const getCacheKey = (url, data) => {
	return url + (data ? JSON.stringify(data) : "")
}

// No-op functions kept for API compatibility
const clearCachePattern = () => {}
const clearAllCache = () => {}
const resetAdminStatus = () => {}

// Create axios instance for better control
const axiosInstance = axios.create({
	baseURL: process.env.REACT_APP_API_PATH,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
	},
})

// Fire-and-forget tracking writes get their own instance, deliberately without
// the response interceptor below. That interceptor signs the student out on a
// 401 and raises a permanent (autoClose: false) error toast on a 5xx -- correct
// for a request the student is waiting on, wrong for a click log nothing
// visible depends on. A failing audit write must be silent to the student.
const trackingInstance = axios.create({
	baseURL: process.env.REACT_APP_API_PATH,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
	},
})

const revealOutbox = createRevealAnswerOutbox({
	send: (questionId, payload, token) =>
		trackingInstance.post(`/questions/${questionId}/reveal-answer`, payload, {
			headers: { Authorization: "Bearer " + token },
			timeout: 10000,
			withCredentials: false,
		}),
	storage: {
		keys: () => Object.keys(localStorage),
		getItem: key => localStorage.getItem(key),
		setItem: (key, value) => localStorage.setItem(key, value),
		removeItem: key => localStorage.removeItem(key),
	},
})
// Whether the session that just 401'd was an admin viewing as another account.
// Same question, same answer as the banner and the billing controls ask --
// utils/viewAs.js owns it, and is defensive because this runs inside an error
// handler where the store shape is `{}` once signed out.
const wasViewingAs = store => isViewingAs(store.getState())

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
	function(response) {
		return response
	},
	function(error) {
		// Handle network errors (no response)
		if (!error.response) {
			return Promise.reject(error)
		}

		const status = error.response.status

		if (status === 401) {
			const store = getStore()
			const actions = getAuthActions()
			if (error.response.data && error.response.data.message === "The token has been blacklisted") {
				store.dispatch(actions.toggleSingeDeviceNotification())
			}
			// A View As session that ended underneath the admin rather than
			// because they pressed Stop -- the two-hour expiry ran out, or the
			// account's owner logged in and revoked the token
			// (../wicked-maths-laravel/docs/VIEW_AS_USER.md).
			//
			// Read BEFORE signOut, which clears the state this reads. Without
			// it they are dropped on the login screen with no explanation for
			// why they were signed out of an account that was not theirs.
			if (wasViewingAs(store)) {
				toast.error(VIEW_AS.sessionEnded, { autoClose: false })
			}
			store.dispatch(actions.signOut())
		}

		if (status >= 500) {
			const message = error.response.data && error.response.data.message
			toast.error(message || "Server error", {
				autoClose: false,
			})
		}

		return Promise.reject(error)
	},
)

const serialize = obj => {
	return !_.isEmpty(obj)
		? "?" +
				Object.keys(obj)
					.reduce(function(array, key) {
						array.push(key + "=" + encodeURIComponent(obj[key]))
						return array
					}, [])
					.join("&")
		: ""
}

const get = (url, data = {}, params = {}) => {
	const fullUrl = url + serialize(data)
	const cacheKey = getCacheKey(fullUrl, null)

	// Deduplicate concurrent identical requests (prevents double-fetching)
	if (pendingRequests.has(cacheKey)) {
		return pendingRequests.get(cacheKey)
	}

	// Make the request directly to the server - no caching
	const requestPromise = axiosInstance
		.get(fullUrl, params)
		.then(response => {
			pendingRequests.delete(cacheKey)
			return response
		})
		.catch(error => {
			pendingRequests.delete(cacheKey)
			throw error
		})

	pendingRequests.set(cacheKey, requestPromise)
	return requestPromise
}

const post = (url, data = {}) => {
	return axiosInstance.post(url, data)
}

// Never rejects: a tracking write that fails is not the student's problem.
const postSilent = (url, data = {}) => trackingInstance.post(url, data).catch(() => {})

const put = (url, data = {}) => {
	return axiosInstance.put(url, data)
}

// PATCH, not PUT, for a partial update. The leagues endpoints are routed as
// PATCH server-side (rename or pin a round, toggle one entry's exclusion) and
// Laravel's router matches on the verb -- sending PUT at a PATCH route is a 405,
// not a lenient fallback.
const patch = (url, data = {}) => {
	return axiosInstance.patch(url, data)
}

const del = url => {
	return axiosInstance.delete(url)
}

let authToken = null
const setAuthToken = token => {
	authToken = token
	revealOutbox.setSession(token)
	if (token) {
		axiosInstance.defaults.headers.common["Authorization"] = "Bearer " + token
		trackingInstance.defaults.headers.common["Authorization"] = "Bearer " + token
	} else {
		delete axiosInstance.defaults.headers.common.Authorization
		delete trackingInstance.defaults.headers.common.Authorization
	}
}

// Runs at module scope, and practically every module reaches request.js, so an
// exception here is not a failed request -- it is an uncaught throw during import
// that leaves the user on a blank white screen with no route back except clearing
// site data by hand. A localStorage entry can be corrupt (a truncated write, a
// quota failure, a stale format from an older build), so read it defensively:
// worst case the user is treated as signed out, which they can recover from.
const readPersistedToken = () => {
	try {
		const accountData = JSON.parse(localStorage.getItem("account"))
		return accountData ? accountData.token : null
	} catch (e) {
		return null
	}
}

setAuthToken(readPersistedToken())

// A sibling tab changed accounts. Pause this tab, never adopt its new identity
// while this tab still displays the previous account's questions.
const handleAccountStorageChange = value => {
	let token = null
	try {
		const account = JSON.parse(value)
		token = account ? account.token : null
	} catch (e) {
		/* Treat corrupt state as signed out. */
	}
	if (token !== authToken) setAuthToken(null)
}

const request = {
	get,
	post,
	postSilent,
	postRevealAnswer: revealOutbox.record,
	revealAnswerDeliveryState: revealOutbox.deliveryState,
	resumeRevealAnswers: revealOutbox.resume,
	put,
	patch,
	delete: del,
	setAuthToken,
	handleAccountStorageChange,
	clearCachePattern,
	clearAllCache,
	resetAdminStatus,
}

export default request
